import { Link } from "react-router-dom";
import { timeAgo } from "@/lib/api";
import { StatusChip } from "./RegulationRadar";
import { PolicyHead, SubthemeChip } from "./PolicyIntelligence";
import { sectorTrack } from "./SectorHero";
import { CountryFlag } from "@/components/CountryFlag";

const Stat = ({ v, label }: { v: number | string; label: string }) => (
  <div className="min-w-0">
    <p className="font-mono text-[15px] font-bold text-white leading-none">{v}</p>
    <p className="font-mono text-[7.5px] uppercase tracking-[0.14em] text-gray-500 mt-1">{label}</p>
  </div>
);

const JCard = ({ j }: { j: any }) => (
  <div data-testid={`jurisdiction-intel-${j.code}`}
    className="rounded-xl border border-white/[0.08] bg-[#0a0b10] p-4 sm:p-5 flex flex-col">
    <div className="flex items-center gap-3">
      <CountryFlag country={j.jurisdiction} size={40} rounded="rounded-lg" />
      <div className="min-w-0">
        <p className="text-white font-heading font-bold text-[15px] leading-tight truncate">{j.jurisdiction}</p>
        {(j.statuses || []).length > 0 && (
          <div className="flex gap-1.5 mt-1">
            {j.statuses.map((s: string) => <StatusChip key={s} status={s} />)}
          </div>
        )}
      </div>
    </div>
    <div className="grid grid-cols-3 gap-3 rounded-lg border border-white/[0.06] bg-white/[0.02] px-3.5 py-3 mt-4">
      <Stat v={(j.regulations || []).length} label="Tracked" />
      <Stat v={j.linked_coverage ?? 0} label="Linked" />
      <Stat v={j.stories_7d ?? 0} label="Stories · 7D" />
    </div>
    {j.top_story && (
      <div className="mt-4">
        <p className="font-mono text-[8px] font-bold uppercase tracking-[0.22em] text-[#00E5FF]">Top Development · 7D</p>
        <Link to={`/article/${j.top_story.slug}`}
          onClick={() => sectorTrack("jurisdiction_top_story_click", j.top_story.slug, "policy")}
          className="group block mt-1.5">
          <span className="flex items-center gap-2">
            <SubthemeChip s={j.top_story.subtheme} />
            <span className="font-mono text-[8.5px] uppercase tracking-wide text-gray-600">{timeAgo(j.top_story.published_at)}</span>
          </span>
          <span className="block text-gray-200 font-semibold text-[13px] leading-snug mt-1.5 line-clamp-2 group-hover:text-[#A78BFA] transition-colors">
            {j.top_story.title}
          </span>
        </Link>
      </div>
    )}
    <div className="flex flex-wrap items-center gap-1.5 mt-auto pt-4">
      {(j.agencies || []).map((a: string) => (
        <span key={a} className="rounded-full border border-white/[0.12] bg-white/[0.04] px-2 py-0.5 font-mono text-[9px] uppercase tracking-wide text-gray-400">
          {a}
        </span>
      ))}
      {j.last_structured_update && (
        <span className="ml-auto font-mono text-[8px] uppercase tracking-wide text-gray-600"
          title="Most recent update to a structured Regulation Tracker record in this jurisdiction">
          Structured · {timeAgo(j.last_structured_update)}
        </span>
      )}
    </div>
  </div>
);

/* JURISDICTION INTELLIGENCE (§11-12): the three real tracked jurisdictions, deeper —
   statuses, coverage stats, top validated development, regulators. Only real fields;
   architecture supports more jurisdictions without redesign (jurisdiction_slug reserved). */
export const JurisdictionIntelligence = ({ items }: { items: any[] }) => {
  if (!items?.length) return null;
  return (
    <section data-testid="policy-jurisdiction-intel" className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-12">
      <PolicyHead title="Jurisdiction Intelligence" testid="jurisdiction-intel-head"
        desc="Structured records, validated coverage and the most important recent development — per tracked jurisdiction." />
      <div className={`grid gap-4 sm:grid-cols-2 ${items.length >= 3 ? "lg:grid-cols-3" : ""}`}>
        {items.map((j) => <JCard key={j.jurisdiction} j={j} />)}
      </div>
    </section>
  );
};
